import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGXLogger } from 'ngx-logger';
import { Observable, map, switchMap } from 'rxjs';

import { assertExists } from 'common';
import { Search } from 'models';
import { BaseMenuComponent } from 'src/app/common/base-menu-component';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { SearchesQuery } from 'src/app/state/searches/searches.query';

/** Describes the event emitted when a related search is selected. */
export interface RelatedSearchSelectedEvent {
  /** Selected search. */
  search: Search;
  /** Whether it should be opened in a new tab. */
  openInNewTab: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-related-searches-menu',
  templateUrl: './related-searches-menu.component.html',
  styleUrls: ['./related-searches-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RelatedSearchesMenuComponent
  extends BaseMenuComponent
  implements OnInit
{
  /** Event emitted when a search is selected. */
  @Output()
  relatedSearchSelected = new EventEmitter<RelatedSearchSelectedEvent>();
  /** Observable array of searches. */
  searches$: Observable<Search[]>;

  private archiveId: number;
  private databaseId: number;

  constructor(
    private logger: NGXLogger,
    private archivesQuery: ArchivesQuery,
    private searchesQuery: SearchesQuery
  ) {
    super();
  }

  ngOnInit(): void {
    this.searches$ = this.archivesQuery.archiveRouteParams$.pipe(
      untilDestroyed(this),
      map(([databaseId, archiveId]) => {
        assertExists(databaseId, 'There must be an active database id.');
        assertExists(archiveId, 'There must be an active archive id.');
        this.databaseId = databaseId;
        this.archiveId = archiveId;
        return [databaseId, archiveId];
      }),
      switchMap(([databaseId, archiveId]) => {
        return this.searchesQuery.selectForParent(archiveId);
      })
    );
  }

  /**
   * Handler for the search on click event.
   *
   * @param search Selected search.
   * @param event Pointer event for the click.
   */
  onClickSearch(event: PointerEvent, search: Search): void {
    this.relatedSearchSelected.emit({ search, openInNewTab: event.ctrlKey });
  }
}
